import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import React, { useState, useRef, useEffect } from "react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import Header from "@/components/header";
import MDEditor, { commands } from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import { toast } from "sonner";
import pdfImage from "@/assets/search/pdf.png";
import mdImage from "@/assets/search/ic-MarkDown.svg";
// 新增引入 SimpleMDE 组件和样式
import SimpleMDE from "react-simplemde-editor";
import "easymde/dist/easymde.min.css";
import { useTranslation } from "@/locales";

// 定义文件数据类型
interface FileData {
  name: string;
  size: number;
  status: string;
  uploader: string;
  md5: string;
  file?: File; // 保存 File 对象
  progress?: number; // 添加进度属性
}

export default function uploadMarkdown() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState(false);
  const knowledgeId = location.state?.knowledgeId || "null"; // 获取传入的知识库ID
  console.log("knowledgeId:", knowledgeId);
  const deleteDialogRef = useRef<any>(null);
  const [DialogData, setDialogData] = useState({
    title: "",
    description: "",
    confirmText: "",
  });

  // 在组件挂载后设置翻译
  useEffect(() => {
    setDialogData({
      title: t("common.confirmDelete"),
      description: t("admin.deleteConfirmMessage"),
      confirmText: t("common.confirm"),
    });
  }, []);

  // 新增 Markdown 内容状态
  const [markdownContent, setMarkdownContent] = useState("");

  // 检查是否可以上传（标题不为空且内容长度超过10个字符）
  const canUpload = title.trim().length > 0 && markdownContent.length > 10;

  // 处理上传逻辑
  const handleUpload = async () => {
    // 校验标题
    if (!title.trim()) {
      setTitleError(true);
      return;
    }

    // 内容长度不足10个字符，添加相应提示

    try {
      const body = {
        knowledgeId: Number(knowledgeId),
        title,
        content: markdownContent,
      };
      // 设置content-type为x-www-form-urlencoded
      await api.post("/v1/admin/knowledge/file/markdown/upload", new URLSearchParams(body as any).toString(), {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      // 上传成功后返回上一页或其他操作
      toast.success(t("admin.operationSuccess"));
      navigate(-1);
    } catch (error) {
      console.error(t("admin.uploadFailed"), error);
      // 错误提示
    }
  };

  // 判断当前主题是否是深色模式
  const [isDarkMode, setIsDarkMode] = useState(false);
  // 监听主题切换
  useEffect(() => {
    // 检测当前主题
    console.log(localStorage.getItem("themeMode"));
    const checkTheme = () => {
      const darkMode = document.documentElement.classList.contains("dark") || window.matchMedia("(prefers-color-scheme: dark)").matches;
      setIsDarkMode(darkMode);
      if (localStorage.getItem("themeMode") === "light") {
        setIsDarkMode(false);
      }
    };

    // 初始检测
    checkTheme();

    // 监听主题变化
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // 监听系统主题变化
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const mediaQueryListener = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches);
      if (localStorage.getItem("themeMode") === "light") {
        setIsDarkMode(false);
      }
    };
    mediaQuery.addEventListener("change", mediaQueryListener);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener("change", mediaQueryListener);
    };
  }, []);

  return (
    <main className="pl-4 pr-4 py-12 pt-0 bg-background" style={{ width: "100%" }}>
      <Header title={t("admin.mdFileUpload")} />
      <div className="space-y-4">
        <div className="flex justify-between items-center mx-auto space-y-4 mt-5">
          <h2 className="text-xl font-bold mb-0" style={{ cursor: "pointer" }} onClick={() => navigate(-1)}>
            <span className="whitespace-nowrap" style={{ fontSize: "16px" }}>
              <span style={{ color: "#9c9c9c" }}>{t("admin.fileCollection")}/</span>
              {t("admin.uploadMdFile")}
            </span>
          </h2>
        </div>

        <div className="flex items-center">
          <span className="whitespace-nowrap" style={{ fontSize: "15px" }}>
            {t("common.title")}:
          </span>
          <Input
            style={{ display: "inline-block", marginLeft: "10px", height: "32px", borderColor: titleError ? "red" : undefined }}
            placeholder={t("admin.pleaseEnterTitle")}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (titleError && e.target.value.trim()) {
                setTitleError(false);
              }
            }}
            className="w-100 ml-2"
          />
        </div>

        <div className="ag-theme-alpine" style={{ height: "60vh", width: "100%" }} data-color-mode={isDarkMode ? "dark" : "light"}>
          <MDEditor
            value={markdownContent}
            onChange={setMarkdownContent}
            height={"100%"}
            // 增大工具栏按钮尺寸
            components={{
              toolbar: (command, disabled, executeCommand) => {
                // 判断是否为 fullscreen 按钮
                const isFullscreenButton = command.name === "fullscreen";
                return (
                  <button
                    key={command.name}
                    disabled={disabled}
                    onClick={() => executeCommand(command, command.name)}
                    style={{
                      fontSize: "16px", // 增大字体
                      margin: "0 2px",
                      display: isFullscreenButton ? "none" : undefined,
                    }}
                    title={command.buttonProps?.title}
                  >
                    {command.icon}
                  </button>
                );
              },
            }}
            // commands 属性功能说明：
            // bold: 粗体文本格式
            // italic: 斜体文本格式
            // strikethrough: 删除线文本格式
            // hr: 插入水平分割线
            // title: 标题格式（h1-h6）
            // link: 插入超链接
            // quote: 引用块
            // code: 行内代码
            // codeBlock: 代码块
            // comment: 添加注释
            // unorderedListCommand: 无序列表
            // orderedListCommand: 有序列表
            // checkedListCommand: 任务列表
            // help: 帮助文档
            commands={[
              commands.bold,
              commands.italic,
              commands.strikethrough,
              commands.hr,
              commands.title,
              commands.quote,
              commands.code,
              commands.codeBlock,
              commands.unorderedListCommand,
              commands.orderedListCommand,
              commands.checkedListCommand,
            ]}
          />
        </div>
        <div className="space-x-2">
          <Button onClick={handleUpload} disabled={!canUpload}>
            {t("common.confirm")}
          </Button>
        </div>
      </div>

      {/* 确认弹窗 */}
      <ConfirmDialog ref={deleteDialogRef} title={DialogData.title} description={DialogData.description} confirmText={DialogData.confirmText} onConfirm={() => {}} />
    </main>
  );
}
